/**
 * ==========================================================================
 *  SCREENS: what is up on the thirty monitors in the juhlasali
 * ==========================================================================
 *  A hall full of machines whose screens are flat blue boxes reads as a shop
 *  that has not opened yet. What it has to read as is a room thirty people
 *  fell asleep in four hours ago - and that is a room where everything is
 *  still running: somebody's desktop, somebody's browser, a playlist that ran
 *  out at four in the morning, the machine that has been sitting on a blue
 *  screen since before any of it, and the one nobody is going to admit to.
 *
 *  Drawn onto a canvas at load time for the three reasons `surfaces.ts` gives
 *  - CSP, licensing, and the same pixels on every machine - and drawn as
 *  *layout* rather than as text. At 0.84 m across, seen from two metres away
 *  at a run, a line of body text is a grey bar whatever it says, so it is
 *  drawn as a grey bar. What has to survive the distance is the silhouette:
 *  a sidebar and a green pill is Spotify before you can read a word of it,
 *  and a red rounded rectangle with a triangle in it is YouTube.
 *
 *  Nothing here is lettered with a brand and nothing here is photographic.
 *  The marks are shapes, and the one machine somebody left something on is
 *  mosaicked - which is both the only version of that joke worth drawing and
 *  the funnier one.
 *
 *  One canvas per kind, shared by every machine showing it: thirty separate
 *  512-pixel canvases is fifteen megabytes spent making sure two Spotify
 *  windows at opposite ends of a hall are not the same playlist.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { mulberry32 } from '../utils/prng';
import type { ScreenContent } from '../world/Level';

/** 16:9, which is what every panel in the hall is. */
const W = 512;
const H = 288;

type Ctx = CanvasRenderingContext2D;

/* --------------------------------------------------------------- paint --- */

function rounded(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function fillRounded(
  ctx: Ctx,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
): void {
  ctx.fillStyle = fill;
  rounded(ctx, x, y, w, h, r);
  ctx.fill();
}

/**
 * A run of body text.
 *
 * Every line is a bar, and the last one is short - which is the one detail
 * that makes a stack of rectangles read as a paragraph rather than as a bar
 * chart lying on its side.
 */
function textBlock(
  ctx: Ctx,
  x: number,
  y: number,
  width: number,
  lines: number,
  color: string,
  lineHeight = 9,
  thickness = 4,
): void {
  ctx.fillStyle = color;
  for (let i = 0; i < lines; i++) {
    const last = i === lines - 1;
    ctx.fillRect(x, y + i * lineHeight, last ? width * 0.55 : width, thickness);
  }
}

/** The triangle in a play button, pointing right, fitted to a box. */
function playTriangle(ctx: Ctx, cx: number, cy: number, size: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx - size * 0.35, cy - size * 0.5);
  ctx.lineTo(cx + size * 0.5, cy);
  ctx.lineTo(cx - size * 0.35, cy + size * 0.5);
  ctx.closePath();
  ctx.fill();
}

/**
 * The last pass over every screen: scanlines, a corner vignette and a sheen
 * across the top left.
 *
 * This is the whole of what separates "a picture" from "a picture on a panel".
 * The monitors are drawn unlit, so nothing else in the renderer is ever going
 * to put a reflection on one - it has to be in the texture or it does not
 * happen at all.
 */
function panel(ctx: Ctx): void {
  ctx.fillStyle = 'rgba(0,0,0,0.16)';
  for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);

  const sheen = ctx.createLinearGradient(0, 0, W * 0.75, H);
  sheen.addColorStop(0, 'rgba(255,255,255,0.10)');
  sheen.addColorStop(0.35, 'rgba(255,255,255,0.02)');
  sheen.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, W, H);

  const vignette = ctx.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, W * 0.72);
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.4)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, W, H);
}

/* ------------------------------------------------------------- windows --- */

/**
 * A desktop nobody has put anything on: the wallpaper, four icons in the
 * corner and the taskbar down the middle of the bottom edge.
 *
 * The centred taskbar is the whole tell. Every Windows before this one put the
 * start button in the left corner, so a bar of icons floating in the middle of
 * the bottom of the screen is the one shape that dates the machine.
 */
function drawWindows(ctx: Ctx): void {
  const sky = ctx.createLinearGradient(0, 0, W * 0.4, H);
  sky.addColorStop(0, '#0a2b63');
  sky.addColorStop(0.6, '#12489c');
  sky.addColorStop(1, '#071c44');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  // The bloom: petals thrown round a hot centre, which is near enough to the
  // stock wallpaper to be recognised and nowhere near close enough to be it.
  const cx = W * 0.54;
  const cy = H * 0.44;
  ctx.save();
  ctx.translate(cx, cy);
  for (let i = 0; i < 7; i++) {
    ctx.rotate((Math.PI * 2) / 7);
    const petal = ctx.createLinearGradient(0, 0, 0, -H * 0.42);
    petal.addColorStop(0, 'rgba(120,200,255,0.30)');
    petal.addColorStop(1, 'rgba(40,90,190,0)');
    ctx.fillStyle = petal;
    ctx.beginPath();
    ctx.ellipse(0, -H * 0.2, H * 0.07, H * 0.22, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, H * 0.3);
  glow.addColorStop(0, 'rgba(190,230,255,0.55)');
  glow.addColorStop(1, 'rgba(190,230,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Desktop icons, top left, in the column they land in on a fresh install.
  const icons = ['#e6c15c', '#e6c15c', '#b8c6d4', '#7fd0a8'];
  icons.forEach((color, i) => {
    const y = 18 + i * 46;
    fillRounded(ctx, 20, y, 26, 20, 4, color);
    textBlock(ctx, 14, y + 25, 38, 1, 'rgba(255,255,255,0.7)', 8, 4);
  });

  // The taskbar: translucent, centred, and the start button first.
  ctx.fillStyle = 'rgba(14,20,34,0.82)';
  ctx.fillRect(0, H - 30, W, 30);
  const bar = ['#4aa3ff', '#e8eaed', '#e8b24a', '#6fcf97', '#7f8cff', '#e06060'];
  bar.forEach((color, i) => {
    const x = W / 2 - (bar.length * 30) / 2 + i * 30;
    if (i === 0) {
      // Four panes rather than one square, so the start button is the logo.
      for (const [dx, dy] of [
        [0, 0],
        [10, 0],
        [0, 10],
        [10, 10],
      ]) {
        fillRounded(ctx, x + 5 + dx, H - 24 + dy, 8, 8, 1, color);
      }
    } else {
      fillRounded(ctx, x + 5, H - 24, 18, 18, 4, color);
    }
  });
  // The clock, in the corner it has always been in.
  textBlock(ctx, W - 44, H - 21, 30, 2, 'rgba(255,255,255,0.75)', 8, 3);
}

/* ------------------------------------------------------------- browser --- */

/** A browser with too many tabs open, on a page nobody is reading. */
function drawBrowser(ctx: Ctx): void {
  ctx.fillStyle = '#f4f5f7';
  ctx.fillRect(0, 0, W, H);

  // Chrome: the tab strip, then the address bar.
  ctx.fillStyle = '#d8dce2';
  ctx.fillRect(0, 0, W, 44);
  const tabs = [
    { x: 8, w: 120, active: false },
    { x: 132, w: 120, active: true },
    { x: 256, w: 120, active: false },
    { x: 380, w: 90, active: false },
  ];
  for (const tab of tabs) {
    fillRounded(ctx, tab.x, 4, tab.w, 20, 6, tab.active ? '#f4f5f7' : '#c6ccd4');
    fillRounded(ctx, tab.x + 8, 11, 7, 7, 2, '#7b8794');
    textBlock(ctx, tab.x + 21, 12, tab.w - 34, 1, '#7b8794', 8, 4);
  }
  fillRounded(ctx, 8, 28, W - 60, 12, 6, '#ffffff');
  fillRounded(ctx, 15, 32, 5, 5, 2, '#5aa469');
  textBlock(ctx, 26, 32, 190, 1, '#98a2ae', 8, 4);

  // The page: a masthead, a picture, and columns of something.
  ctx.fillStyle = '#1f4e9c';
  ctx.fillRect(0, 44, W, 34);
  textBlock(ctx, 16, 56, 96, 1, 'rgba(255,255,255,0.92)', 8, 8);
  for (let i = 0; i < 4; i++) {
    textBlock(ctx, 220 + i * 62, 58, 44, 1, 'rgba(255,255,255,0.6)', 8, 4);
  }

  ctx.fillStyle = '#c9d3de';
  ctx.fillRect(16, 92, 200, 112);
  playTriangle(ctx, 116, 148, 34, 'rgba(255,255,255,0.85)');
  textBlock(ctx, 16, 214, 200, 3, '#8d97a3', 12, 5);

  textBlock(ctx, 232, 92, 264, 1, '#2b3440', 8, 10);
  textBlock(ctx, 232, 112, 264, 7, '#aab3bd', 12, 5);
  fillRounded(ctx, 232, 206, 92, 18, 4, '#1f4e9c');
  textBlock(ctx, 244, 213, 60, 1, 'rgba(255,255,255,0.85)', 8, 4);
}

/* ------------------------------------------------------------- youtube --- */

/** A video paused four hours ago, and the column of what to watch after it. */
function drawYoutube(ctx: Ctx): void {
  ctx.fillStyle = '#0f0f0f';
  ctx.fillRect(0, 0, W, H);

  // The mark: a red rounded rectangle with a triangle in it, and a search pill.
  fillRounded(ctx, 14, 10, 26, 18, 5, '#e02f2f');
  playTriangle(ctx, 27, 19, 9, '#ffffff');
  fillRounded(ctx, 150, 11, 210, 16, 8, '#1f1f1f');
  textBlock(ctx, 160, 17, 90, 1, '#6f6f6f', 8, 4);
  fillRounded(ctx, W - 40, 11, 16, 16, 8, '#3b6ea8');

  // The player. Something dark with a light in it - a demo, or a stream of one.
  const stage = { x: 14, y: 40, w: 322, h: 181 };
  ctx.fillStyle = '#05070a';
  ctx.fillRect(stage.x, stage.y, stage.w, stage.h);
  const spot = ctx.createRadialGradient(
    stage.x + stage.w * 0.42,
    stage.y + stage.h * 0.45,
    4,
    stage.x + stage.w * 0.42,
    stage.y + stage.h * 0.45,
    stage.h * 0.7,
  );
  spot.addColorStop(0, 'rgba(120,200,255,0.85)');
  spot.addColorStop(0.4, 'rgba(60,90,200,0.35)');
  spot.addColorStop(1, 'rgba(10,10,20,0)');
  ctx.fillStyle = spot;
  ctx.fillRect(stage.x, stage.y, stage.w, stage.h);
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(stage.x, stage.y + stage.h - 44, stage.w, 44);
  // Scrubber: two thirds through, with the handle on the join.
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.fillRect(stage.x + 8, stage.y + stage.h - 20, stage.w - 16, 3);
  ctx.fillStyle = '#e02f2f';
  ctx.fillRect(stage.x + 8, stage.y + stage.h - 20, (stage.w - 16) * 0.66, 3);
  ctx.beginPath();
  ctx.arc(stage.x + 8 + (stage.w - 16) * 0.66, stage.y + stage.h - 18.5, 5, 0, Math.PI * 2);
  ctx.fill();
  playTriangle(ctx, stage.x + 16, stage.y + stage.h - 33, 10, '#ffffff');
  textBlock(ctx, stage.x + 30, stage.y + stage.h - 36, 46, 1, 'rgba(255,255,255,0.8)', 8, 4);

  textBlock(ctx, 14, 232, 300, 1, '#e8e8e8', 8, 8);
  textBlock(ctx, 14, 250, 180, 2, '#767676', 11, 4);

  // Up next.
  for (let i = 0; i < 5; i++) {
    const y = 40 + i * 44;
    ctx.fillStyle = '#242424';
    ctx.fillRect(348, y, 68, 38);
    ctx.fillStyle = i % 2 === 0 ? '#3d5a80' : '#5a4a3d';
    ctx.fillRect(348, y, 68, 38);
    textBlock(ctx, 422, y + 4, 76, 2, '#d0d0d0', 10, 4);
    textBlock(ctx, 422, y + 26, 48, 1, '#6f6f6f', 8, 3);
  }
}

/* ------------------------------------------------------------- spotify --- */

/** The playlist that ran out somewhere around four, still on the last track. */
function drawSpotify(ctx: Ctx): void {
  ctx.fillStyle = '#121212';
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, 118, H - 40);
  // The mark: three arcs over a green disc, no lettering.
  ctx.fillStyle = '#1db954';
  ctx.beginPath();
  ctx.arc(28, 24, 12, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 2.4;
  for (const r of [4, 7, 10]) {
    ctx.beginPath();
    ctx.arc(24, 30, r, -Math.PI * 0.85, -Math.PI * 0.15);
    ctx.stroke();
  }
  textBlock(ctx, 12, 54, 84, 3, '#b3b3b3', 16, 5);
  ctx.fillStyle = '#282828';
  ctx.fillRect(12, 110, 94, 1);
  textBlock(ctx, 12, 122, 84, 6, '#6a6a6a', 14, 4);

  // The room the playlist is in: the cover, the title, and the tracks.
  const wash = ctx.createLinearGradient(0, 0, 0, 150);
  wash.addColorStop(0, '#2f5d46');
  wash.addColorStop(1, '#121212');
  ctx.fillStyle = wash;
  ctx.fillRect(118, 0, W - 118, 150);
  const cover = ctx.createLinearGradient(134, 20, 210, 96);
  cover.addColorStop(0, '#7ad0a0');
  cover.addColorStop(1, '#1f6b46');
  ctx.fillStyle = cover;
  ctx.fillRect(134, 20, 76, 76);
  textBlock(ctx, 222, 30, 60, 1, '#c8c8c8', 8, 4);
  textBlock(ctx, 222, 44, 210, 1, '#ffffff', 8, 14);
  textBlock(ctx, 222, 74, 130, 1, '#b3b3b3', 8, 4);
  fillRounded(ctx, 134, 106, 34, 20, 10, '#1db954');
  playTriangle(ctx, 151, 116, 10, '#0b0b0b');

  for (let i = 0; i < 6; i++) {
    const y = 142 + i * 17;
    const playing = i === 3;
    if (playing) {
      ctx.fillStyle = '#1c1c1c';
      ctx.fillRect(126, y - 4, W - 140, 16);
    }
    textBlock(ctx, 134, y, 8, 1, '#6a6a6a', 8, 4);
    textBlock(ctx, 152, y, 150 + ((i * 37) % 90), 1, playing ? '#1db954' : '#e0e0e0', 8, 4);
    textBlock(ctx, W - 44, y, 26, 1, '#6a6a6a', 8, 4);
  }

  // Now playing.
  ctx.fillStyle = '#181818';
  ctx.fillRect(0, H - 40, W, 40);
  ctx.fillStyle = '#1f6b46';
  ctx.fillRect(12, H - 34, 28, 28);
  textBlock(ctx, 48, H - 30, 84, 2, '#c8c8c8', 11, 4);
  playTriangle(ctx, W / 2, H - 28, 12, '#ffffff');
  ctx.fillStyle = '#4d4d4d';
  ctx.fillRect(W / 2 - 110, H - 14, 220, 3);
  ctx.fillStyle = '#b3b3b3';
  ctx.fillRect(W / 2 - 110, H - 14, 176, 3);
}

/* ---------------------------------------------------------------- peli --- */

/**
 * The thing the hall is here for, seen from inside it: a corridor, a
 * crosshair, and the HUD round the edges.
 *
 * Two walls in perspective and a floor plane is all a shooter needs to read as
 * one - what actually says "somebody is mid-round" is the scoreboard at the
 * top and the ammo count in the corner.
 */
function drawGame(ctx: Ctx): void {
  ctx.fillStyle = '#6f7d8c';
  ctx.fillRect(0, 0, W, H * 0.44);
  ctx.fillStyle = '#8c7a55';
  ctx.fillRect(0, H * 0.44, W, H);

  // Two walls running to a vanishing point dead centre, so the far doorway
  // lands under the crosshair: what this is a picture of is the half-second
  // somebody is being aimed at, not an empty corridor.
  const vx = W / 2;
  const vy = H * 0.44;
  ctx.fillStyle = '#b8a377';
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(vx - 90, vy - 26);
  ctx.lineTo(vx - 90, vy + 54);
  ctx.lineTo(0, H);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#9e8a63';
  ctx.beginPath();
  ctx.moveTo(W, 0);
  ctx.lineTo(vx + 96, vy - 22);
  ctx.lineTo(vx + 96, vy + 48);
  ctx.lineTo(W, H);
  ctx.closePath();
  ctx.fill();
  // The far end of it, and a doorway in the far end of it.
  ctx.fillStyle = '#7d6d4e';
  ctx.fillRect(vx - 90, vy - 26, 186, 80);
  ctx.fillStyle = '#2a2419';
  ctx.fillRect(vx - 16, vy - 6, 34, 60);

  // Somebody standing in it, lit from the side he is coming out of - because
  // an empty corridor is a screensaver and this is a final.
  ctx.fillStyle = '#5d6470';
  ctx.fillRect(vx - 7, vy + 8, 14, 30);
  ctx.beginPath();
  ctx.arc(vx, vy + 4, 5.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#20242c';
  ctx.fillRect(vx - 7, vy + 8, 6, 30);
  // And the hit marker over him, which is the whole of the story.
  ctx.strokeStyle = 'rgba(255,90,90,0.95)';
  ctx.lineWidth = 2;
  for (const [sx, sy] of [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ]) {
    ctx.beginPath();
    ctx.moveTo(vx + sx * 7, vy + 14 + sy * 7);
    ctx.lineTo(vx + sx * 14, vy + 14 + sy * 14);
    ctx.stroke();
  }

  // The gun, bottom right, held the way every one of them is.
  ctx.fillStyle = '#31353c';
  ctx.beginPath();
  ctx.moveTo(W * 0.62, H);
  ctx.lineTo(W * 0.78, H * 0.62);
  ctx.lineTo(W * 0.9, H * 0.66);
  ctx.lineTo(W * 0.86, H);
  ctx.closePath();
  ctx.fill();

  // Crosshair.
  ctx.strokeStyle = 'rgba(180,255,180,0.9)';
  ctx.lineWidth = 2;
  for (const [dx, dy] of [
    [-12, 0],
    [12, 0],
    [0, -12],
    [0, 12],
  ]) {
    ctx.beginPath();
    ctx.moveTo(W / 2 + dx * 0.42, H / 2 + dy * 0.42);
    ctx.lineTo(W / 2 + dx, H / 2 + dy);
    ctx.stroke();
  }

  // Scoreboard and round timer, top centre.
  fillRounded(ctx, W / 2 - 62, 6, 124, 22, 4, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = '#5aa4e0';
  ctx.fillRect(W / 2 - 56, 12, 22, 11);
  ctx.fillStyle = '#e0a24a';
  ctx.fillRect(W / 2 + 34, 12, 22, 11);
  textBlock(ctx, W / 2 - 18, 13, 36, 1, 'rgba(255,255,255,0.9)', 8, 9);

  // Health and ammo.
  fillRounded(ctx, 14, H - 34, 96, 20, 3, 'rgba(0,0,0,0.5)');
  ctx.fillStyle = '#5fd07a';
  ctx.fillRect(20, H - 28, 62, 8);
  fillRounded(ctx, W - 110, H - 34, 96, 20, 3, 'rgba(0,0,0,0.5)');
  textBlock(ctx, W - 100, H - 28, 76, 1, 'rgba(255,255,255,0.85)', 8, 8);
}

/* --------------------------------------------------------------- video --- */

/**
 * The machine nobody is going to admit to, with the tab still open.
 *
 * Drawn as a mosaic on purpose, and not as a euphemism: a grid of skin-tone
 * blocks is what the joke actually is - the shape of the player, the lurid
 * interface round it and the fact that somebody left it up in a room with
 * thirty people in it. There is nothing to draw underneath the blur, and the
 * blur is funnier than anything that could be.
 */
function drawVideo(ctx: Ctx): void {
  ctx.fillStyle = '#0b0a0d';
  ctx.fillRect(0, 0, W, H);

  // The site's own furniture: a black bar with an orange pill in it.
  ctx.fillStyle = '#161318';
  ctx.fillRect(0, 0, W, 22);
  fillRounded(ctx, 12, 5, 58, 13, 3, '#e8a020');
  textBlock(ctx, 78, 10, 40, 1, '#8a8a8a', 8, 4);
  fillRounded(ctx, 300, 5, 130, 13, 6, '#211d24');

  const stage = { x: 26, y: 34, w: 340, h: 190 };
  ctx.fillStyle = '#000000';
  ctx.fillRect(stage.x, stage.y, stage.w, stage.h);

  // The mosaic. Seeded, so it is the same block in the same place on every
  // machine in the world - see the note about determinism in `surfaces.ts`.
  const rng = mulberry32(0x504f524e);
  const cols = 17;
  const rows = 10;
  const bw = stage.w / cols;
  const bh = (stage.h - 34) / rows;
  const tones = ['#c99a76', '#d8ab86', '#b8825f', '#e0bb9a', '#8f5f47', '#a5705a', '#c4736f'];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      // A soft body-shaped mass in the middle of frame and a dark room round
      // it, so the mosaic has a subject rather than being confetti.
      const dx = (col - cols / 2) / (cols / 2);
      const dy = (row - rows / 2) / (rows / 2);
      const inside = dx * dx * 1.1 + dy * dy < 0.62 + rng() * 0.3;
      ctx.fillStyle = inside
        ? tones[Math.floor(rng() * tones.length)]
        : `rgb(${18 + rng() * 26},${12 + rng() * 18},${16 + rng() * 22})`;
      ctx.fillRect(stage.x + col * bw, stage.y + row * bh, bw + 1, bh + 1);
    }
  }

  // The controls, in the colour that site's controls are.
  ctx.fillStyle = 'rgba(0,0,0,0.8)';
  ctx.fillRect(stage.x, stage.y + stage.h - 34, stage.w, 34);
  playTriangle(ctx, stage.x + 16, stage.y + stage.h - 17, 11, '#ffffff');
  ctx.fillStyle = '#3a3440';
  ctx.fillRect(stage.x + 34, stage.y + stage.h - 19, stage.w - 90, 4);
  ctx.fillStyle = '#e8a020';
  ctx.fillRect(stage.x + 34, stage.y + stage.h - 19, (stage.w - 90) * 0.38, 4);
  textBlock(ctx, stage.x + stage.w - 48, stage.y + stage.h - 20, 36, 1, '#b0b0b0', 8, 4);

  // Title, tags, and the column of what is next - all of it mosaicked too.
  textBlock(ctx, 26, 234, 230, 1, '#d8d8d8', 8, 7);
  const tags = [58, 44, 72, 38];
  let tagX = 26;
  for (const width of tags) {
    fillRounded(ctx, tagX, 252, width, 13, 6, '#241f28');
    textBlock(ctx, tagX + 8, 257, width - 16, 1, '#8a7f92', 8, 3);
    tagX += width + 8;
  }
  for (let i = 0; i < 4; i++) {
    const y = 34 + i * 50;
    for (let col = 0; col < 5; col++) {
      for (let row = 0; row < 3; row++) {
        ctx.fillStyle =
          rng() > 0.45 ? tones[Math.floor(rng() * tones.length)] : `rgb(30,22,28)`;
        ctx.fillRect(382 + col * 21, y + row * 12, 22, 13);
      }
    }
    textBlock(ctx, 382, y + 40, 96, 1, '#7a7a7a', 8, 4);
  }
}

/* ---------------------------------------------------------------- bsod --- */

/**
 * The machine that has been like this since before anybody fell asleep.
 *
 * The one screen in the hall with words on it, and they are in Finnish because
 * they are the only ones anybody could read from a metre away - see the rule
 * about player-facing text in CLAUDE.md.
 */
function drawBsod(ctx: Ctx): void {
  ctx.fillStyle = '#1268c8';
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#ffffff';
  ctx.font = '68px Helvetica, Arial, sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillText(':(', 46, 40);

  ctx.font = '15px Helvetica, Arial, sans-serif';
  ctx.fillText('Laitteessa ilmeni ongelma, ja se on', 46, 128);
  ctx.fillText('käynnistettävä uudelleen.', 46, 148);
  ctx.font = '12px Helvetica, Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText('0 % valmiina', 46, 186);

  // The QR nobody has ever scanned.
  const cell = 5;
  const rng = mulberry32(0x50f0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(46, 214, cell * 12, cell * 12);
  ctx.fillStyle = '#1268c8';
  for (let y = 0; y < 12; y++) {
    for (let x = 0; x < 12; x++) {
      if (rng() > 0.5) ctx.fillRect(46 + x * cell, 214 + y * cell, cell, cell);
    }
  }
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = '11px Helvetica, Arial, sans-serif';
  ctx.fillText('Pysäytyskoodi:', 120, 220);
  ctx.fillText('KERNEL_SECURITY_CHECK_FAILURE', 120, 236);
}

/* ------------------------------------------------------------ assembly --- */

const PAINTERS: Record<ScreenContent, (ctx: Ctx) => void> = {
  windows: drawWindows,
  browser: drawBrowser,
  youtube: drawYoutube,
  spotify: drawSpotify,
  game: drawGame,
  video: drawVideo,
  bsod: drawBsod,
};

/**
 * The picture for one kind of screen.
 *
 * Callers are expected to cache by `content` - `World` does. Nothing here
 * varies per machine, which is the point: see the file header.
 */
export function buildScreenTexture(content: ScreenContent): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    PAINTERS[content](ctx);
    panel(ctx);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
